import type { Condition } from '@/config/utils'
import type { AllColumnSuggestionItem } from '@/types/columnValueSource'
import type { OrganizationDevice, OrganizationDeviceFilters } from '@/types/organization'
import { asList } from '@/utils/asList'
import type { AxiosRequestConfig } from 'axios'
import { apiCall, apiClient, getTenantFromUrl, isOwnerFromStoredAuth } from './axiosInstance'

export type DeviceState = Record<string, unknown>

export type DeviceMetadata = Record<string, unknown>

export interface DependencyObject {
	dashboards?: Array<string | number>
	exporters?: Array<string | number>
	session_rules?: Array<string | number>
	dbc_parser?: Array<string | number>
	device_state?: Array<string | number>
}

/**
 * Shape of each item returned by GET /v3/metadata-keys. The endpoint now returns
 * objects carrying the key `name` plus its `dependency` list (resources that
 * reference the key, e.g. ['dashboard']), instead of a bare string array.
 */
export interface MetadataKeyResponse {
	name: string
	dependency: DependencyObject
}

export interface DeviceActionType {
	action_id: number
	action_state: string
	created_at: string
	created_by: string
	device_id: string
	device_reported_state: string | null
	errors: string | null
	payload: Record<string, unknown>
	progress: number | null
	retry_count: number
	timeout_at: string | null
	type: string
	updated_at: string
	/** Only populated for `renew_cert` actions. Distinguishes attach/detach/transfer reprovisioning. */
	reprovision_type?: { reprovision_type?: string }
}

export interface Device {
	id: string
	status: DeviceStatus.Active | DeviceStatus.Inactive
	metadata: DeviceMetadata
	action: DeviceActionType
	state: DeviceState
	[key: string]: unknown
}

export type Filters = {
	[key: string]: MetadataFilterValueType[]
}

export interface FilterOptions {
	[filterName: string]: string[] | number[]
}

export type MetadataFilterValueType = string | null
export type SearchDevicesFiltersType = {
	metaDatafilters?: Record<string, MetadataFilterValueType | MetadataFilterValueType[]>
	stateFilters?: Condition[]
}

export interface SearchDeviceResponse {
	devices: Device[]
	count: number
}

export interface SearchDeviceIdsResponse {
	ids: string[]
	count: number
}

export interface DeviceSearchTimestampValue {
	timestamp: string
}

export interface DeviceSearchDateValue {
	date: string
}

export type DeviceSearchPrimitive = string | number | boolean

export type DeviceSearchValue = DeviceSearchPrimitive | DeviceSearchTimestampValue | DeviceSearchDateValue

export interface DeviceFieldCondition {
	eq?: DeviceSearchValue
	ne?: DeviceSearchValue
	gt?: DeviceSearchValue
	gte?: DeviceSearchValue
	lt?: DeviceSearchValue
	lte?: DeviceSearchValue
	in?: DeviceSearchValue[]
	is_null?: boolean
	regex?: string
}

export interface DeviceMetadataFilter extends DeviceFieldCondition {
	column: string
}

export interface DeviceStateFilter extends DeviceFieldCondition {
	column: string
}

export type DeviceIdFilter = DeviceFieldCondition

/** Recursive filter type matching the search API spec */
export type DeviceSearchFilters =
	| { and: DeviceSearchFilters[] }
	| { or: DeviceSearchFilters[] }
	| { not: DeviceSearchFilters }
	| { metadata: DeviceMetadataFilter }
	| { state: DeviceStateFilter }
	| { id: DeviceIdFilter }
	// Org-level: filter by project. `project_id` matches devices.tenant_id (the project id). "project" is an
	// accepted backend alias; we send project_id.
	| { project_id: DeviceFieldCondition }
	// Org-level: filter by device uid (bigint) — values must be numbers.
	| { uid: DeviceFieldCondition }
	| boolean

export type DeviceSearchSortBy = 'id' | 'uid' | { metadata: string } | { state: string }

export type SortOrder = 'asc' | 'desc'

export enum DeviceStatus {
	Active = 'active',
	Inactive = 'inactive',
	All = 'all',
}

/**
 * Device visibility. The backend has no flag of its own for it yet, so a device the user archived is
 * stored as `active = false` and the only way to list archived devices alongside the rest is
 * `status=all`. Callers passing `DeviceStatus.All` have always meant "the All tab", which is narrowed
 * to active, so that narrowing stays and archived devices are opted into per request. A status other
 * than `All` is never widened, which keeps callers that ask for `active` immune. Once the backend
 * grows a real visibility param, only this function and its callers' argument change.
 */
/**
 * `/v3/devices/ids` answers one page. The backend defaults `per_page` to 100 and caps it at 1000
 * (`default_device_ids_per_page` in platform/src/endpoints/devices.rs), while `count` is a separate
 * unpaginated COUNT(*) — so a caller reading `ids` as the whole match set silently gets the first
 * hundred. The helpers below walk the pages instead.
 */
const DEVICE_IDS_PAGE_SIZE = 1000

/**
 * Ceiling on one walk, so a select-all over a six figure fleet cannot spin. Past it the id list is
 * short of `count`, which every response still reports honestly, so a caller can tell.
 */
const DEVICE_IDS_MAX_PAGES = 20

const toFetchStatus = (status: DeviceStatus, includeArchived = false): DeviceStatus => {
	if (status !== DeviceStatus.All) {
		return status
	}
	return includeArchived ? DeviceStatus.All : DeviceStatus.Active
}

/**
 * One row from `searchDeviceIdsWithFields`. `uid` is omitted on purpose — the backend sends an i64
 * and this endpoint is parsed without json-bigint, so it can lose precision.
 */
export interface DeviceIdsWithFields {
	id: string
	/** Only present when fields were requested; holds exactly those keys. */
	metadata?: DeviceMetadata
	/** Only present when fields were requested; holds exactly those keys. */
	state?: DeviceState
}

export interface DeviceDataDownloadField {
	kind: 'metadata' | 'state'
	name: string
}

export interface DeviceDataDownloadRequest {
	fields: DeviceDataDownloadField[]
	filters?: DeviceSearchFilters
	status?: DeviceStatus
}

export interface DeviceDataDownload {
	blob: Blob
	filename: string
}

/** Backend hard limit on POST /v3/devices/configurations/download — requests over this count are rejected with a 400. */
export const DEVICE_CONFIGURATIONS_DOWNLOAD_LIMIT = 100

export interface DeviceConfigurationsDownload {
	blob: Blob
	filename: string
}

export function validateDeviceDataDownloadRequest(request: DeviceDataDownloadRequest): void {
	if (request.fields.length === 0) {
		throw new Error('Select at least one metadata or state field to download')
	}
	const names = new Set<string>()
	for (const field of request.fields) {
		if (!field.name.trim()) {
			throw new Error('Download field names cannot be empty')
		}
		if (names.has(field.name)) {
			throw new Error(`Download field names must be unique: ${field.name}`)
		}
		names.add(field.name)
	}
}

/** Full payload for POST /v3/devices/search */
export interface DeviceSearchPayload {
	page: number
	per_page: number
	filters?: DeviceSearchFilters | null
	sort_by?: DeviceSearchSortBy
	sort_order?: SortOrder
	status?: DeviceStatus
}

interface BulkDeviceStatusRequest extends DeviceSearchPayload {
	status: DeviceStatus
}

export interface BulkDeviceStatusResponse {
	updated: number
}

/**
 * Explicit device ids, or the filter a select-all resolved to. Never nullable: the bulk endpoint
 * updates every device matching `filters` + `status` with no row limit, so a request that carries no
 * filter rewrites the whole project. Pass `true` to mean that on purpose.
 */
export type BulkDeviceStatusTarget = string[] | DeviceSearchFilters

function buildBulkDeviceStatusRequest(
	target: BulkDeviceStatusTarget,
	status: DeviceStatus.Active | DeviceStatus.Inactive,
): BulkDeviceStatusRequest {
	if (Array.isArray(target) && target.length === 0) {
		throw new Error('No devices selected')
	}
	return {
		page: 1,
		per_page: 1000,
		status,
		filters: Array.isArray(target) ? { id: { in: target } } : target,
		sort_by: 'id',
		sort_order: 'asc',
	}
}

function updateDevicesBulkV3(
	action: 'activate' | 'deactivate',
	target: BulkDeviceStatusTarget,
	tenantId?: string,
): Promise<BulkDeviceStatusResponse> {
	return apiCall(`${V3_DEVICES_ENDPOINT}/${action}-bulk`, {
		method: 'POST',
		data: buildBulkDeviceStatusRequest(target, action === 'activate' ? DeviceStatus.Inactive : DeviceStatus.Active),
		...(tenantId && {
			headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' },
		}),
	})
}

interface OrganizationDeviceListResponse {
	count: number
	result: OrganizationDevice[]
}

interface OrganizationDeviceSearchParams {
	page?: number
	perPage?: number
	filters?: DeviceSearchFilters | null
	sortBy?: DeviceSearchSortBy
	sortOrder?: SortOrder
	status?: DeviceStatus
	tenant?: string
}

export interface DeviceCertificateResponse {
	project_id: string
	broker: string
	port: number
	device_id: string
	authentication: {
		ca_certificate: string
		device_certificate: string
		device_private_key: string
	}
	http_credentials?: {
		api_key: string
		api_url: string
	}
}

export interface DeviceFilterOption {
	filterName: string
	filterValues: (string | number)[]
	kind?: 'id' | 'uid' | 'metadata' | 'state' | 'project'
	count?: number
}

export interface FilterOptionsProject {
	id: string
	is_org: boolean
	name: string
}

export interface FilterOptionsApiResponse {
	ids: string[]
	metadata: Record<string, string[]>
	state: Record<string, (string | number | boolean | null)[]>
	/** Distinct device UIDs (org-level inventory). Absent at project level. */
	uids?: (string | number)[]
	/** Project choices for org-level inventory. Absent at project level. */
	projects?: FilterOptionsProject[]
	counts?: {
		ids: number
		uids?: number
		metadata: Record<string, number>
		state: Record<string, number>
	}
}

/** Column kinds the filter-options endpoint can paginate/search on a single column. */
export type FilterOptionsColumnKind = 'all' | 'id' | 'uid' | 'metadata' | 'state'

/** `column` payload for the single-column filter-options mode. `name` required for metadata/state. */
export interface FilterOptionsColumnPayload {
	kind: FilterOptionsColumnKind
	name?: string
	search?: string
}

/** One page of distinct values for a single column, plus the total distinct count (for load-more math). */
export interface ColumnFilterOptionsResult {
	values: (string | number)[]
	count: number
}

/** Raw single-column filter-options response: `{ column, values, count }`. */
interface ColumnFilterOptionsApiResponse {
	column: FilterOptionsColumnPayload
	values: (string | number | boolean | null)[]
	count: number
}

export interface GetColumnFilterOptionsArgs {
	column: FilterOptionsColumnPayload
	/** 1-based page. */
	page?: number
	/** Backend max is 1000. */
	perPage?: number
	/** Server-side ILIKE substring match on the column. */
	search?: string
	status?: DeviceStatus
	/** Current active filters so suggestions reflect the applied filter context. */
	filters?: DeviceSearchFilters | null
	/** Explicit project tenant (injected as x-bytebeam-tenant). Ignored when organizationId is set. */
	tenantId?: string
	/** When set, hits the org-level endpoint instead of the project-level one. */
	organizationId?: string
	signal?: AbortSignal
}

/** Coerce backend values (state values may be JSON numbers/booleans) to the (string|number) list the UI uses. */
function normalizeColumnFilterValues(values: (string | number | boolean | null)[]): (string | number)[] {
	const result: (string | number)[] = []
	for (const value of values) {
		if (value === null || value === undefined) {
			continue
		}
		result.push(typeof value === 'boolean' ? String(value) : value)
	}
	return result
}

export function transformFilterOptionsResponse(response: FilterOptionsApiResponse): DeviceFilterOption[] {
	const deviceOptions: DeviceFilterOption[] = []

	if (response.ids?.length) {
		deviceOptions.push({
			filterName: 'id',
			filterValues: response.ids,
			kind: 'id',
			count: response.counts?.ids,
		})
	}

	if (response.uids?.length) {
		deviceOptions.push({
			filterName: 'uid',
			filterValues: response.uids,
			kind: 'uid',
			count: response.counts?.uids ?? 0,
		})
	}

	if (response.metadata) {
		for (const [key, values] of Object.entries(response.metadata)) {
			deviceOptions.push({
				filterName: key,
				filterValues: values,
				kind: 'metadata',
				count: response.counts?.metadata?.[key],
			})
		}
	}

	const projectNames = response.projects?.filter(project => !project.is_org).map(project => project.name) ?? []
	if (projectNames.length) {
		deviceOptions.push({
			filterName: 'project',
			filterValues: projectNames,
			kind: 'project',
			count: projectNames.length ?? 0,
		})
	}

	if (response.state) {
		for (const [key, values] of Object.entries(response.state)) {
			const filteredValues = values.filter((v): v is string | number => v !== null && v !== undefined)
			deviceOptions.push({
				filterName: key,
				filterValues: filteredValues,
				kind: 'state',
				count: response.counts?.state?.[key],
			})
		}
	}

	return deviceOptions
}

export interface CreateDevicePayload {
	id: string
	lifetime: number | null
	generate_mtls_creds: boolean
	generate_http_creds: boolean
}

/** Request body for POST /v3/devices/bulk (serde deny_unknown_fields — only these keys). */
export interface ProvisionDevicesBulkRequest {
	count: number
	lifetime?: number
	generate_mtls_creds: boolean
	generate_http_creds: boolean
	// TODO(withHttpServer): Plumb `with_http_server` (bool) on create/bulk. Response-shaping only: if true, each device in the response includes `http_server` (value from `DEVICE_API_DOMAIN` / `device_api_domain`); if false or omitted, the key is not returned. This is the HTTP ingest host for building stream submit URLs, not a request body field for the domain. Independent of `generate_http_creds` (which only adds `http_credentials`).
}

/** One element of the 200 JSON array from POST /v3/devices/bulk */
export interface ProvisionedDeviceBulkItem {
	device_id: string
	project_id: string
	broker?: string
	port?: number
	authentication?: {
		ca_certificate: string
		device_certificate: string
		device_private_key: string
	}
	http_credentials?: {
		api_url: string
		api_key: string
	}
	http_server?: string
}

import { TIMEOUT_NONE } from '@/api/timeouts'

const V3_DEVICES_ENDPOINT = '/v3/devices'

export const deviceApi = {
	/**
	 * Generate certificates for a device by (re)provisioning it via the V3 devices endpoint.
	 *
	 * @param deviceId - Device ID
	 * @param lifetime - Certificate lifetime in seconds (default: 315360000 = 10 years)
	 * @param tenantId - When provided (org-level / Inventory), scopes the request to that tenant
	 */
	generateDeviceCertificates: async (
		deviceId: number | string,
		lifetime: number | null = 315360000,
		tenantId?: string,
	): Promise<DeviceCertificateResponse> => {
		const payload: CreateDevicePayload = {
			id: String(deviceId),
			lifetime,
			generate_mtls_creds: true,
			generate_http_creds: false,
		}
		const options: AxiosRequestConfig = {
			method: 'POST',
			data: payload,
			...(tenantId && {
				headers: {
					'x-bytebeam-tenant': tenantId,
					'x-bytebeam-impersonate': 'admin',
				},
			}),
		}
		return apiCall(V3_DEVICES_ENDPOINT, options)
	},
	getDeviceFilterOptions: async (
		tenantId?: string,
		status: DeviceStatus = DeviceStatus.Active,
	): Promise<DeviceFilterOption[]> => {
		const response = await apiCall<FilterOptionsApiResponse>('/v3/devices/filter-options', {
			method: 'POST',
			data: { status: toFetchStatus(status) },
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId } }),
		})
		return transformFilterOptionsResponse(response)
	},
	/** Fetch one page of a single column's distinct values (server pagination + ILIKE search). Used for
	 *  lazy search-bar suggestions; pass organizationId for org level. */
	getDeviceColumnFilterOptions: async ({
		column,
		page = 1,
		perPage = 1000,
		search,
		status = DeviceStatus.Active,
		filters,
		tenantId,
		organizationId,
		signal,
	}: GetColumnFilterOptionsArgs): Promise<ColumnFilterOptionsResult> => {
		const trimmedSearch = search?.trim()
		const data: Record<string, unknown> = { status: toFetchStatus(status), page, per_page: perPage, column }
		if (trimmedSearch) {
			data.search = trimmedSearch
		}
		if (filters !== undefined && filters !== null) {
			data.filters = filters
		}
		const url = organizationId
			? `/v3/organizations/${encodeURIComponent(organizationId)}/devices/filter-options`
			: '/v3/devices/filter-options'
		const response = await apiCall<ColumnFilterOptionsApiResponse>(url, {
			method: 'POST',
			data,
			signal,
			// Org owner scoping a sub-project has no direct role there; impersonation grants tenant admin
			...(!organizationId &&
				tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		})
		return {
			values: normalizeColumnFilterValues(response.values ?? []),
			count: response.count ?? 0,
		}
	},
	/** Search across all columns (id, metadata, state) for matching values using kind: 'all'. */
	getDeviceAllColumnFilterOptions: async ({
		search,
		perPage = 10,
		status = DeviceStatus.Active,
		filters,
		tenantId,
		organizationId,
		signal,
	}: {
		search: string
		perPage?: number
		status?: DeviceStatus
		filters?: DeviceSearchFilters | null
		tenantId?: string
		organizationId?: string
		signal?: AbortSignal
	}): Promise<AllColumnSuggestionItem[]> => {
		const trimmedSearch = search.trim()
		if (!trimmedSearch) {
			return []
		}
		const data: Record<string, unknown> = {
			status: toFetchStatus(status),
			per_page: perPage,
			column: { kind: 'all', search: trimmedSearch },
		}
		if (filters !== undefined && filters !== null) {
			data.filters = filters
		}
		const url = organizationId
			? `/v3/organizations/${encodeURIComponent(organizationId)}/devices/filter-options`
			: '/v3/devices/filter-options'
		const response = await apiCall<FilterOptionsApiResponse>(url, {
			method: 'POST',
			data,
			signal,
			...(!organizationId &&
				tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		})

		const results: AllColumnSuggestionItem[] = []
		const appendValues = (fieldId: string, values: unknown) => {
			if (!Array.isArray(values)) {
				return
			}
			for (const v of values) {
				if (v !== null && v !== undefined && String(v).trim() !== '') {
					results.push({ fieldId, value: String(v) })
				}
			}
		}

		appendValues('id', response.ids)
		appendValues('uid', response.uids)

		if (response.metadata && typeof response.metadata === 'object') {
			for (const [key, vals] of Object.entries(response.metadata)) {
				appendValues(`metadata.${key}`, vals)
			}
		}
		if (response.state && typeof response.state === 'object') {
			for (const [key, vals] of Object.entries(response.state)) {
				appendValues(`state.${key}`, vals)
			}
		}
		return results
	},

	/**
	 * `downloadDeviceData` as a stream, for `downloadFileWithStreaming`. Bypasses axios, so it sets
	 * the tenant and impersonation headers by the interceptor's own rules.
	 */
	downloadDeviceDataStream: async (
		request: DeviceDataDownloadRequest,
		options?: { signal?: AbortSignal; tenantId?: string },
	): Promise<Response> => {
		validateDeviceDataDownloadRequest(request)
		const tenantId = options?.tenantId
		const tenant = tenantId || getTenantFromUrl()
		return fetch('/api/v3/devices/download', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...(tenant && { 'x-bytebeam-tenant': tenant }),
				// The interceptor drops this header for non-owners; a plain fetch must too.
				...(tenantId && isOwnerFromStoredAuth() && { 'x-bytebeam-impersonate': 'admin' }),
			},
			body: JSON.stringify(request),
			credentials: 'include',
			signal: options?.signal,
		})
	},

	downloadDeviceData: async (request: DeviceDataDownloadRequest, tenantId?: string): Promise<DeviceDataDownload> => {
		validateDeviceDataDownloadRequest(request)
		const response = await apiClient.post<Blob>('/v3/devices/download', request, {
			responseType: 'blob',
			timeout: TIMEOUT_NONE,
			validateStatus: () => true,
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		})
		if (response.status < 200 || response.status >= 300) {
			let message = 'Failed to download device data'
			try {
				const body = JSON.parse(await response.data.text()) as { error_message?: unknown }
				if (typeof body.error_message === 'string' && body.error_message.trim()) {
					message = body.error_message
				}
			} catch {
				// The backend normally returns JSON errors; keep the fallback for malformed proxy responses.
			}
			throw new Error(message)
		}

		const disposition = response.headers['content-disposition']
		const filenameMatch = typeof disposition === 'string' ? disposition.match(/filename="?([^";]+)"?/i) : null
		return { blob: response.data, filename: filenameMatch?.[1] ?? 'devices.csv' }
	},
	/**
	 * Bulk-download device configurations (mTLS certs) as a zip.
	 * POST /v3/devices/configurations/download
	 * Backend rejects requests matching more than DEVICE_CONFIGURATIONS_DOWNLOAD_LIMIT devices with a 400.
	 */
	downloadDeviceConfigurations: async (
		deviceIds: string[],
		tenantId?: string,
	): Promise<DeviceConfigurationsDownload> => {
		const payload: DeviceSearchPayload = {
			page: 1,
			per_page: DEVICE_CONFIGURATIONS_DOWNLOAD_LIMIT,
			filters: { id: { in: deviceIds } },
			sort_by: 'id',
			sort_order: 'asc',
			status: DeviceStatus.Active,
		}
		const response = await apiClient.post<Blob>('/v3/devices/configurations/download', payload, {
			responseType: 'blob',
			timeout: TIMEOUT_NONE,
			validateStatus: () => true,
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		})
		if (response.status < 200 || response.status >= 300) {
			let message = 'Failed to download device configurations'
			try {
				const body = JSON.parse(await response.data.text()) as { error_message?: unknown }
				if (typeof body.error_message === 'string' && body.error_message.trim()) {
					message = body.error_message
				}
			} catch {
				// The backend normally returns JSON errors; keep the fallback for malformed proxy responses.
			}
			throw new Error(message)
		}

		const disposition = response.headers['content-disposition']
		const filenameMatch = typeof disposition === 'string' ? disposition.match(/filename="?([^";]+)"?/i) : null
		return { blob: response.data, filename: filenameMatch?.[1] ?? 'device-configurations.zip' }
	},
	/**
	 * Create/provision a new device (V3 API)
	 * POST /v3/devices
	 * @param payload - Device creation payload matching ProvisionDeviceSchema
	 */
	createDevice: async (payload: CreateDevicePayload, tenantId?: string): Promise<DeviceCertificateResponse> => {
		const options: AxiosRequestConfig = {
			method: 'POST',
			data: payload,
			...(tenantId && {
				headers: {
					'x-bytebeam-tenant': tenantId,
					'x-bytebeam-impersonate': 'admin',
				},
			}),
		}
		return apiCall(V3_DEVICES_ENDPOINT, options)
	},

	/**
	 * Bulk provision devices (V3 API)
	 * POST /v3/devices/bulk
	 * @param tenantId - Required tenant (project) id; inventory callers should pass root tenant id from `useProjectsStore`.
	 */
	bulkCreateDevices: async (
		params: ProvisionDevicesBulkRequest,
		tenantId: string,
	): Promise<ProvisionedDeviceBulkItem[]> => {
		const needsLifetime = params.generate_mtls_creds || params.generate_http_creds
		const data: Record<string, unknown> = {
			count: params.count,
			generate_mtls_creds: params.generate_mtls_creds,
			generate_http_creds: params.generate_http_creds,
		}
		if (needsLifetime) {
			if (params.lifetime === undefined) {
				throw new Error('lifetime is required when generating mTLS or HTTP credentials')
			}
			data.lifetime = params.lifetime
		}
		return apiCall(`${V3_DEVICES_ENDPOINT}/bulk`, {
			method: 'POST',
			data,
			headers: {
				'x-bytebeam-tenant': tenantId,
				'x-bytebeam-impersonate': 'admin',
			},
			// Certs are never persisted server-side, so aborting loses credentials the backend already issued.
			timeout: TIMEOUT_NONE,
		})
	},

	/**
	 * Get devices with pagination and filtering (V3 API)
	 * POST /v3/devices/search
	 * Replacement for v2/devices endpoint
	 * Supports recursive filter objects, sorting by id/metadata/state columns
	 * @param tenantId - When provided, requests are scoped to that tenant (org-level); passes x-bytebeam-tenant and x-bytebeam-impersonate headers
	 */
	// TODO: refactor to options-object pattern (9 positional params; callers pass multiple `undefined` placeholders to reach `signal`)
	getDevicesV3: async (
		page = 1,
		perPage = 10,
		status: DeviceStatus = DeviceStatus.All,
		tenantId?: string,
		filters?: DeviceSearchFilters | null,
		sortBy?: DeviceSearchSortBy,
		sortOrder?: SortOrder,
		signal?: AbortSignal,
		includeArchived = false,
	): Promise<SearchDeviceResponse> => {
		const payload: DeviceSearchPayload = {
			page,
			per_page: perPage,
			status: toFetchStatus(status, includeArchived),
			sort_by: sortBy ?? 'uid',
			sort_order: sortOrder ?? 'asc',
		}

		if (filters !== undefined && filters !== null) {
			payload.filters = filters
		}

		const options: AxiosRequestConfig = {
			method: 'POST',
			data: payload,
			signal,
			...(tenantId && {
				headers: {
					'x-bytebeam-tenant': tenantId,
					'x-bytebeam-impersonate': 'admin',
				},
			}),
		}
		return apiCall('/v3/devices/search', options)
	},

	/**
	 * Get all device IDs the current user can access (V3 API)
	 * GET /v3/devices/ids
	 * Returns ids filtered by the caller's permissions, sorted ascending. Paged internally up to
	 * `DEVICE_IDS_MAX_PAGES`; this response carries no count, so it walks until a short page arrives.
	 * @param status - Tab status to scope ids to (active/inactive/all). Backend defaults to 'active' when omitted, so always pass explicitly.
	 * @param tenantId - When provided (org-level / Inventory), scopes the request to that tenant
	 */
	getDeviceIds: async (
		status: DeviceStatus = DeviceStatus.All,
		tenantId?: string,
		signal?: AbortSignal,
		includeArchived = false,
	): Promise<string[]> => {
		const ids: string[] = []
		for (let page = 1; page <= DEVICE_IDS_MAX_PAGES; page++) {
			const res = await apiCall<{ ids: string[] }>('/v3/devices/ids', {
				params: { status: toFetchStatus(status, includeArchived), page, per_page: DEVICE_IDS_PAGE_SIZE },
				signal,
				...(tenantId && {
					headers: {
						'x-bytebeam-tenant': tenantId,
						'x-bytebeam-impersonate': 'admin',
					},
				}),
			})
			const pageIds = asList(res.ids)
			ids.push(...pageIds)
			if (pageIds.length < DEVICE_IDS_PAGE_SIZE) {
				break
			}
		}
		return ids
	},

	/**
	 * One page of POST /v3/devices/ids with the total. For a caller that needs the count more than the
	 * list, such as a probe over an uploaded id list; `searchDeviceIds` walks every page instead.
	 * @param tenantId - Scopes the request to that tenant from a route at org level, as `getDevicesV3` does.
	 */
	searchDeviceIdsPage: async ({
		status = DeviceStatus.All,
		filters,
		page = 1,
		perPage = DEVICE_IDS_PAGE_SIZE,
		signal,
		includeArchived = false,
		tenantId,
	}: {
		status?: DeviceStatus
		filters?: DeviceSearchFilters | null
		page?: number
		perPage?: number
		signal?: AbortSignal
		includeArchived?: boolean
		tenantId?: string
	}): Promise<SearchDeviceIdsResponse> => {
		const data: Record<string, unknown> = { status: toFetchStatus(status, includeArchived), page, per_page: perPage }
		if (filters !== undefined && filters !== null) {
			data.filters = filters
		}
		const res = await apiCall<{ ids: string[]; count?: number }>('/v3/devices/ids', {
			method: 'POST',
			data,
			signal,
			...(tenantId && {
				headers: {
					'x-bytebeam-tenant': tenantId,
					'x-bytebeam-impersonate': 'admin',
				},
			}),
		})
		const ids = asList(res.ids)
		return { ids, count: typeof res.count === 'number' ? res.count : ids.length }
	},

	/**
	 * Get device ids matching a search filter (V3 API)
	 * POST /v3/devices/ids
	 * Walks every page up to `DEVICE_IDS_MAX_PAGES` and returns them with the total matching count.
	 * `ids.length < count` means the walk hit that ceiling.
	 * @param status - Tab status to scope ids to. Backend defaults to 'active' when omitted, so always pass explicitly.
	 * @param filters - Same DeviceSearchFilters shape as the search endpoint; omit when unfiltered.
	 * @param tenantId - Scopes the request to that tenant from a route at org level, as `getDevicesV3` does.
	 */
	searchDeviceIds: async ({
		status = DeviceStatus.All,
		filters,
		signal,
		includeArchived = false,
		tenantId,
	}: {
		status?: DeviceStatus
		filters?: DeviceSearchFilters | null
		signal?: AbortSignal
		includeArchived?: boolean
		tenantId?: string
	}): Promise<SearchDeviceIdsResponse> => {
		const ids: string[] = []
		let count = 0
		for (let page = 1; page <= DEVICE_IDS_MAX_PAGES; page++) {
			const res = await deviceApi.searchDeviceIdsPage({ status, filters, page, signal, includeArchived, tenantId })
			ids.push(...res.ids)
			if (page === 1) {
				count = res.count
			}
			if (res.ids.length < DEVICE_IDS_PAGE_SIZE || ids.length >= count) {
				break
			}
		}
		return { ids, count }
	},

	/**
	 * `searchDeviceIds` plus chosen metadata/state columns, walked the same way, so a whole fleet
	 * arrives where `/v3/devices/search` would cap at 1000.
	 *
	 * Callers must narrow `metadataFields` to the role's `device-metadata:read` grant: one
	 * ungranted key 403s the entire request.
	 */
	searchDeviceIdsWithFields: async ({
		status = DeviceStatus.All,
		filters,
		metadataFields,
		stateFields,
		signal,
	}: {
		status?: DeviceStatus
		filters?: DeviceSearchFilters | null
		metadataFields?: string[]
		stateFields?: string[]
		signal?: AbortSignal
	}): Promise<DeviceIdsWithFields[]> => {
		// Endpoint is `deny_unknown_fields` — send only keys it declares.
		const data: Record<string, unknown> = { status: toFetchStatus(status) }
		if (filters !== undefined && filters !== null) {
			data.filters = filters
		}
		if (metadataFields && metadataFields.length > 0) {
			data.metadata_fields = metadataFields
		}
		if (stateFields && stateFields.length > 0) {
			data.state_fields = stateFields
		}
		const devices: DeviceIdsWithFields[] = []
		let count = 0
		for (let page = 1; page <= DEVICE_IDS_MAX_PAGES; page++) {
			const res = await apiCall<{ devices?: DeviceIdsWithFields[]; count?: number }>('/v3/devices/ids', {
				method: 'POST',
				data: { ...data, page, per_page: DEVICE_IDS_PAGE_SIZE },
				signal,
			})
			const pageDevices = asList(res.devices)
			devices.push(...pageDevices)
			if (page === 1) {
				count = typeof res.count === 'number' ? res.count : pageDevices.length
			}
			if (pageDevices.length < DEVICE_IDS_PAGE_SIZE || devices.length >= count) {
				break
			}
		}
		return devices
	},

	activateDevicesBulkV3: async (target: BulkDeviceStatusTarget, tenantId?: string): Promise<BulkDeviceStatusResponse> =>
		updateDevicesBulkV3('activate', target, tenantId),

	deactivateDevicesBulkV3: async (
		target: BulkDeviceStatusTarget,
		tenantId?: string,
	): Promise<BulkDeviceStatusResponse> => updateDevicesBulkV3('deactivate', target, tenantId),

	/**
	 * Update device metadata (V3 API)
	 * PATCH /v3/devices/{id}/update
	 * @param deviceId - Device ID (string)
	 * @param metadata - Object with metadata key-value pairs. Use null to clear a value.
	 */
	updateDeviceMetadataV3: async (
		deviceId: string,
		metadata: Record<string, string | null>,
		tenantId?: string,
	): Promise<void> =>
		apiCall(`/v3/devices/${encodeURIComponent(deviceId)}/update`, {
			method: 'PATCH',
			data: { metadata },
			...(tenantId && {
				headers: {
					'x-bytebeam-tenant': tenantId,
					'x-bytebeam-impersonate': 'admin',
				},
			}),
		}),

	/**
	 * Bulk Device Metadata Update (V3 API)
	 * POST /v3/devices/metadata-bulk
	 * @param file - CSV file with device metadata updates
	 * @param tenantId - When set, scopes the request to that tenant (org-level / root project updates)
	 * @returns Promise with number of updated devices
	 */
	updateDeviceMetadataBulkV3: async (file: File, tenantId?: string): Promise<{ updated: number }> => {
		const formData = new FormData()
		formData.append('file', file)
		return apiCall('/v3/devices/metadata-bulk', {
			method: 'POST',
			data: formData,
			timeout: TIMEOUT_NONE,
			headers: {
				'Content-Type': 'multipart/form-data',
				...(tenantId && {
					'x-bytebeam-tenant': tenantId,
					'x-bytebeam-impersonate': 'admin',
				}),
			},
		})
	},

	getAllDeviceMetadataKeys: async (tenantId?: string, impersonateAdmin = true): Promise<MetadataKeyResponse[]> => {
		const headers = tenantId
			? {
					'x-bytebeam-tenant': tenantId,
					...(impersonateAdmin ? { 'x-bytebeam-impersonate': 'admin' } : {}),
				}
			: undefined
		const response = await apiCall<MetadataKeyResponse[]>('/v3/metadata-keys', headers ? { headers } : undefined)
		return response ?? []
	},

	createMetadataKey: async (name: string, tenantId?: string): Promise<{ status: string }> =>
		apiCall(`/v3/metadata-keys/${encodeURIComponent(name)}`, {
			method: 'POST',
			...(tenantId && {
				headers: {
					'x-bytebeam-tenant': tenantId,
					'x-bytebeam-impersonate': 'admin',
				},
			}),
		}),

	updateMetadataKey: async (name: string, newName: string, tenantId?: string): Promise<{ status: string }> =>
		apiCall(`/v3/metadata-keys/${encodeURIComponent(name)}/rename/${encodeURIComponent(newName)}`, {
			method: 'POST',
			...(tenantId && {
				headers: {
					'x-bytebeam-tenant': tenantId,
					'x-bytebeam-impersonate': 'admin',
				},
			}),
		}),

	deleteMetadataKey: async (name: string, tenantId?: string): Promise<{ status: string }> =>
		apiCall(`/v3/metadata-keys/${encodeURIComponent(name)}`, {
			method: 'DELETE',
			...(tenantId && {
				headers: {
					'x-bytebeam-tenant': tenantId,
					'x-bytebeam-impersonate': 'admin',
				},
			}),
		}),

	/**
	 * Get devices across all tenants (root tenant only)
	 * @param filters - Filter options for root tenant devices
	 * @param page - Page number (default: 1)
	 * @param perPage - Items per page (default: 20)
	 * @returns Promise with devices array and total count
	 */
	getOrganizationDevices: async (
		organizationId: string,
		filters?: OrganizationDeviceFilters,
		page = 1,
		perPage = 20,
	): Promise<{ devices: OrganizationDevice[]; count: number }> => {
		const response = await apiCall<OrganizationDeviceListResponse>(
			`/v3/organizations/${encodeURIComponent(organizationId)}/devices`,
			{
				method: 'GET',
				params: {
					page,
					per_page: perPage,
					...(filters?.tenant_id ? { tenant: filters.tenant_id } : {}),
				},
			},
		)
		return {
			devices: response.result ?? [],
			count: response.count ?? 0,
		}
	},

	/**
	 * Search devices across all tenants (organization-level)
	 * @param searchParams - Search parameters including filters, page, and perPage
	 * @returns Promise with devices array and total count
	 */
	searchOrganizationDevices: async (
		organizationId: string,
		searchParams: OrganizationDeviceSearchParams,
		signal?: AbortSignal,
	): Promise<{ devices: OrganizationDevice[]; count: number }> => {
		const payload: DeviceSearchPayload = {
			page: searchParams.page ?? 1,
			per_page: searchParams.perPage ?? 20,
			status: toFetchStatus(searchParams.status ?? DeviceStatus.Active),
			sort_by: searchParams.sortBy ?? 'uid',
			sort_order: searchParams.sortOrder ?? 'asc',
		}

		if (searchParams.filters !== undefined && searchParams.filters !== null) {
			payload.filters = searchParams.filters
		}

		return apiCall(`/v3/organizations/${encodeURIComponent(organizationId)}/devices/search`, {
			method: 'POST',
			data: payload,
			...(searchParams.tenant ? { params: { tenant: searchParams.tenant } } : {}),
			signal,
		}) as Promise<{ devices: OrganizationDevice[]; count: number }>
	},
	getOrgDeviceFilterOptions: async (organizationId: string, status: DeviceStatus = DeviceStatus.Active) => {
		const response = await apiCall<FilterOptionsApiResponse>(
			`/v3/organizations/${encodeURIComponent(organizationId)}/devices/filter-options`,
			{ method: 'POST', data: { status: toFetchStatus(status) } },
		)
		return transformFilterOptionsResponse(response)
	},
	/**
	 * GET /v3/organizations/{id}/devices/ids
	 * Unlike the project ids endpoint this one has its own handler (org_owners.rs `fetch_device_ids`),
	 * takes only `status` and `filters`, rejects `page` and `per_page` outright, and answers the
	 * whole org in one response.
	 */
	getOrganizationDeviceIds: async (
		organizationId: string,
		status: DeviceStatus = DeviceStatus.All,
		signal?: AbortSignal,
	): Promise<string[]> => {
		const res = await apiCall<{ ids: string[] }>(
			`/v3/organizations/${encodeURIComponent(organizationId)}/devices/ids`,
			{ params: { status: toFetchStatus(status) }, signal },
		)
		return asList(res.ids)
	},

	/**
	 * Get all org device row ids matching a search filter (V3 API)
	 * POST /v3/organizations/{id}/devices/ids
	 * Permission-filtered server-side. Own handler (org_owners.rs `search_device_ids`): unpaginated,
	 * no `count`, and `page` or `per_page` in the body is a 400. Backs select-all across pages at org level.
	 * The response's `ids` field is deduped by device id alone (a device can appear under multiple
	 * tenants at org level), so row ids are built from `devices` as `${id}-${tenant_id}` to match
	 * the composite row id the org-level table uses. Raw `{id, tenant_id, uid}` pairs are also
	 * returned so callers can group by tenant or build detach/reprovision payloads without a
	 * full Device fetch.
	 */
	searchOrganizationDeviceIds: async ({
		organizationId,
		status = DeviceStatus.All,
		filters,
		signal,
	}: {
		organizationId: string
		status?: DeviceStatus
		filters?: DeviceSearchFilters | null
		signal?: AbortSignal
	}): Promise<{ rowIds: string[]; pairs: { id: string; tenant_id: string; uid: number | null }[] }> => {
		const data: Record<string, unknown> = { status: toFetchStatus(status) }
		if (filters !== undefined && filters !== null) {
			data.filters = filters
		}
		const res = await apiCall<{ devices: { id: string; tenant_id: string; uid: number | string | null }[] }>(
			`/v3/organizations/${encodeURIComponent(organizationId)}/devices/ids`,
			{ method: 'POST', data, signal },
		)
		const devices = asList(res.devices)
		// uid is an i64 server-side but can arrive JSON-stringified — normalize it here.
		const pairs = devices.map(device => {
			const uid = device.uid == null || device.uid === '' ? Number.NaN : Number(device.uid)
			return { id: device.id, tenant_id: device.tenant_id, uid: Number.isFinite(uid) ? uid : null }
		})
		return { rowIds: pairs.map(device => `${device.id}-${device.tenant_id}`), pairs }
	},
}
